import React, { useMemo } from 'react';
import {
  Apartment,
  Business,
  Description,
  Email,
  Feedback,
  Home,
  People,
  TrendingUp,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import { useGetAgentsByAdminQuery } from '@/apis/extended/admin/agentApi';
import { useGetProductsByAdminQuery } from '@/apis/extended/admin/productApi';
import { useGetUsersByAdminQuery } from '@/apis/extended/admin/userApi';
import { useGetAllContractsForAdminQuery } from '@/apis/extended/contractApi';
import { AdminLenderDefaults } from '@/components/Admin/LenderDefaults/AdminLenderDefaults';
import { ProfilePersonalInformation } from '@/components/Profile/ProfilePersonalInformation';
import { ProfileSecurity } from '@/components/Profile/ProfileSecurity';
import GlassButton from '@/components/ui/GlassButton';
import GlassCard from '@/components/ui/GlassCard';

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: usersData, isLoading: usersLoading } = useGetUsersByAdminQuery({
    offset: 0,
    limit: 1000,
  });
  const { data: productsData, isLoading: productsLoading } =
    useGetProductsByAdminQuery({ offset: 0, limit: 1 });
  const { data: agentsData, isLoading: agentsLoading } =
    useGetAgentsByAdminQuery({ offset: 0, limit: 1 });
  const { data: contractsData, isLoading: contractsLoading } =
    useGetAllContractsForAdminQuery();

  const activeContractsCount = useMemo(() => {
    if (!contractsData) return 0;
    const inactiveStatuses = new Set(['completed', 'cancelled', 'voided']);
    return contractsData.filter((contract) => {
      const status = contract.contractStatus || 'draft';
      return !inactiveStatuses.has(status);
    }).length;
  }, [contractsData]);

  const ratifiedContractsCount = useMemo(() => {
    if (!contractsData) return 0;
    return contractsData.filter(
      (contract) =>
        contract.contractStatus === 'ratified' || contract.isRatified
    ).length;
  }, [contractsData]);

  const totalUsersCount = useMemo(
    () => usersData?.count ?? 0,
    [usersData?.count]
  );

  const averageLoanAmountPerUser = useMemo(() => {
    if (!contractsData || contractsData.length === 0) return 0;
    const totals = contractsData.reduce(
      (acc, contract) => {
        const loanAmount = Number(contract.offerSpecifics?.loanAmount);
        const offerPrice = Number(contract.offerSpecifics?.offerPrice);
        const downPayment = Number(contract.offerSpecifics?.downPayment);
        const fallbackLoan =
          Number.isFinite(offerPrice) && Number.isFinite(downPayment)
            ? offerPrice - downPayment
            : NaN;
        const safeLoanAmount = Number.isFinite(loanAmount)
          ? loanAmount
          : Number.isFinite(fallbackLoan)
          ? fallbackLoan
          : 0;
        acc.totalLoanAmount += safeLoanAmount;
        return acc;
      },
      { totalLoanAmount: 0 }
    );

    return totals.totalLoanAmount / contractsData.length;
  }, [contractsData]);

  const totalPotentialLoanVolume = useMemo(() => {
    if (!totalUsersCount || !averageLoanAmountPerUser) return 0;
    return totalUsersCount * averageLoanAmountPerUser;
  }, [totalUsersCount, averageLoanAmountPerUser]);

  const formatCurrency = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }),
    []
  );

  /** Light, distinct gradients for stat tiles on the white admin shell */
  const adminStatCards = useMemo(
    () => [
      {
        key: 'users',
        title: 'Total Users',
        gradient: `linear-gradient(145deg, ${alpha(
          theme.palette.primary.main,
          0.11
        )} 0%, ${alpha(theme.palette.primary.light, 0.5)} 48%, ${alpha(
          theme.palette.primaryContainer,
          0.09
        )} 100%)`,
        border: alpha(theme.palette.primary.main, 0.2),
        valueColor: theme.palette.primary.main,
        loading: usersLoading,
        value: usersLoading
          ? null
          : (usersData?.count ?? 0).toLocaleString('en-US'),
      },
      {
        key: 'activeContracts',
        title: 'Active Contracts',
        gradient: `linear-gradient(145deg, ${alpha(
          theme.palette.secondary.main,
          0.12
        )} 0%, ${alpha('#3d5a9e', 0.08)} 50%, ${alpha(
          theme.palette.primary.light,
          0.35
        )} 100%)`,
        border: alpha(theme.palette.secondary.main, 0.22),
        valueColor: theme.palette.secondary.main,
        loading: contractsLoading,
        value: contractsLoading
          ? null
          : activeContractsCount.toLocaleString('en-US'),
      },
      {
        key: 'properties',
        title: 'Properties',
        gradient: `linear-gradient(145deg, ${alpha(
          theme.palette.success.main,
          0.1
        )} 0%, ${alpha(
          theme.palette.success.light ?? '#a3f69c',
          0.35
        )} 55%, ${alpha(theme.palette.success.main, 0.06)} 100%)`,
        border: alpha(theme.palette.success.main, 0.25),
        valueColor: theme.palette.success.main,
        loading: productsLoading,
        value: productsLoading
          ? null
          : (productsData?.count ?? 0).toLocaleString('en-US'),
      },
      {
        key: 'agents',
        title: 'Agents',
        gradient: `linear-gradient(145deg, ${alpha(
          '#5c4d7a',
          0.12
        )} 0%, ${alpha('#8b7aad', 0.18)} 48%, ${alpha('#c4b5e0', 0.25)} 100%)`,
        border: alpha('#5c4d7a', 0.22),
        valueColor: '#3d2f55',
        loading: agentsLoading,
        value: agentsLoading
          ? null
          : (agentsData?.count ?? 0).toLocaleString('en-US'),
      },
      {
        key: 'ratified',
        title: 'Ratified Contracts',
        gradient: `linear-gradient(145deg, ${alpha('#c2410c', 0.1)} 0%, ${alpha(
          '#f59e0b',
          0.16
        )} 52%, ${alpha('#fbbf24', 0.12)} 100%)`,
        border: alpha('#d97706', 0.28),
        valueColor: '#b45309',
        loading: contractsLoading,
        value: contractsLoading
          ? null
          : ratifiedContractsCount.toLocaleString('en-US'),
      },
      {
        key: 'volume',
        title: 'Total Potential Loan Volume',
        gradient: `linear-gradient(145deg, ${alpha('#0e7490', 0.1)} 0%, ${alpha(
          '#14b8a6',
          0.14
        )} 50%, ${alpha('#5eead4', 0.2)} 100%)`,
        border: alpha('#0f766e', 0.26),
        valueColor: '#0f766e',
        loading: usersLoading || contractsLoading,
        value:
          usersLoading || contractsLoading
            ? null
            : formatCurrency.format(totalPotentialLoanVolume),
      },
    ],
    [
      theme,
      usersLoading,
      contractsLoading,
      productsLoading,
      agentsLoading,
      usersData?.count,
      activeContractsCount,
      productsData?.count,
      agentsData?.count,
      ratifiedContractsCount,
      totalPotentialLoanVolume,
      formatCurrency,
    ]
  );

  const adminSections = [
    {
      title: 'User Pipeline Management',
      description: 'Track lead-to-approval pipeline stages',
      icon: <People />,
      path: '/admin/user',
      color: 'primary',
    },
    {
      title: 'Estimate Management',
      description: 'Review property estimates and details',
      icon: <Home />,
      path: '/admin/product',
      color: 'success',
    },
    {
      title: 'Document Management',
      description: 'Review contract document uploads',
      icon: <Description />,
      path: '/admin/documents',
      color: 'secondary',
    },
    {
      title: 'Contract Management',
      description: 'Manage contract submissions and status',
      icon: <Apartment />,
      path: '/admin/properties',
      color: 'info',
    },
    {
      title: 'Agent Assignment',
      description: 'Assign agents to contracts',
      icon: <Business />,
      path: '/admin/agent',
      color: 'warning',
    },
    {
      title: 'Messenger',
      description: 'View and send messages by property',
      icon: <Feedback />,
      path: '/admin/feedback',
      color: 'default',
    },
    {
      title: 'Email Management',
      description: 'Configure system and contract emails',
      icon: <Email />,
      path: '/admin/email',
      color: 'error',
    },
    {
      title: 'Interest Rates',
      description: 'Manage rates and fee configurations',
      icon: <TrendingUp />,
      path: '/admin/interest-rates',
      color: 'primary',
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Quick Stats */}
      <Grid
        container
        spacing={3}
        sx={{
          mb: 4,
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          overflowX: { xs: 'auto', sm: 'visible' },
          pb: { xs: 1, sm: 0 },
        }}
      >
        {adminStatCards.map((stat) => (
          <Grid
            key={stat.key}
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              minWidth: {
                xs: stat.key === 'volume' ? 260 : 220,
                sm: 'auto',
              },
              flex: { xs: '0 0 auto', sm: '1 1 0' },
            }}
          >
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 3,
                border: `1px solid ${stat.border}`,
                background: stat.gradient,
                boxShadow: `0 4px 20px ${alpha(
                  theme.palette.common.black,
                  0.06
                )}`,
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                '@media (hover: hover)': {
                  '&:hover': {
                    boxShadow: `0 8px 28px ${alpha(
                      theme.palette.common.black,
                      0.09
                    )}`,
                    transform: 'translateY(-2px)',
                  },
                },
              }}
            >
              <CardContent
                sx={{ py: 2.5, px: 2.25, '&:last-child': { pb: 2.5 } }}
              >
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    textTransform: 'none',
                    mb: 0.75,
                  }}
                >
                  {stat.title}
                </Typography>
                <Typography
                  variant="h3"
                  component="div"
                  sx={{
                    color: stat.valueColor,
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                  }}
                >
                  {stat.loading ? '—' : stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <GlassCard variant="medium" sx={{ mt: 4 }}>
        <CardContent>
          <AdminLenderDefaults />
          <Box sx={{ mt: 3, display: 'grid', gap: 3 }}>
            <ProfilePersonalInformation />
            <ProfileSecurity />
          </Box>
        </CardContent>
      </GlassCard>
    </Box>
  );
};

export default AdminDashboard;
